/**
 * The campaign's shift scripts (IMPLEMENTATION_PLAN.md A6).
 *
 * Every spawn bearing sits inside the open approach corridor. The rig masks roughly
 * -108..-80, +6..+63 and +82..+110 (measured, see schema.ts), and a vessel arriving in
 * one of those is behind the derrick or the process modules where the player can neither
 * watch it resolve nor shoot along its line. `validateShift` enforces this.
 *
 * Engagement range is the other hard constraint, and it is set by the weapon rather than
 * by taste. The 30 mm scatters about 2.5 mrad, so against a 9 m speedboat it lands
 * roughly 60% of rounds at 1 km, 28% at 2 km and 7% at 4 km. Staging an attack at 4 km
 * therefore asks the player to do something the gun cannot do, and the first version of
 * these scripts did exactly that — the tanker was sunk at 3.9 km in shift 3 having never
 * been defensible. Attacks are now triggered around 2.5-3 km and staged from under
 * 2.5 km, so the whole fight happens inside the envelope where the gun works.
 *
 * Trigger timeouts are generous on purpose. They exist so a shift cannot wedge when the
 * ship a beat is watching gets sunk on the way in — they are not a pacing device. Set too
 * tight they expire while the tanker is still far out and stage the ambush early anyway,
 * which silently defeats the range condition. That is exactly what was happening: a
 * 240 s timeout fired at 4 km on a six-minute approach.
 *
 * Escalation is authored, not emergent: each shift introduces exactly one new idea and
 * gives the player a quiet beat to absorb it before the next arrives.
 */

import type { ShiftScript } from '../schema';
import { openingFor, openingBeats } from './openings';

/**
 * Shift 1 doubles as the tutorial. The tanker spawns at 3.5 km rather than the usual
 * 9–11 km so every guided step completes inside ~3 minutes and its safe arrival lands as
 * the shift's natural climax; the distant contacts exist purely to dress the horizon and
 * sell the scale.
 */
const shift01: ShiftScript = {
  index: 1,
  name: 'First Watch',
  weather: 'overcastDay',
  seed: 'shift-01',
  maxDuration: 1200,
  beats: [
    { kind: 'objective', trigger: { at: 0 }, text: 'Survey the sector' },
    {
      kind: 'radio', trigger: { at: 3 }, speaker: 'RIG CONTROL',
      text: 'Deepwater Actual, you have the watch. Tanker Ardent inbound, three point five kilometres.',
      read: '[calm] Deepwater Actual, you have the watch. [pause] Tanker Ardent inbound, three point five kilometres.',
    },
    {
      kind: 'spawn', trigger: { at: 5 }, tag: 'ardent', vessel: 'tanker',
      count: 1, rangeKm: 3.5, bearingDeg: -22, cargo: 'oil',
    },
    { kind: 'objective', trigger: { at: 8 }, text: 'Escort Ardent to the safety zone' },
    /*
     * One skiff, early, and the tutorial's reason for existing.
     *
     * Shift 1's raiders used to arrive at 78 s, gated on Ardent's range — but the guided
     * steps run out at about twelve seconds, and step six says "open fire". With nothing
     * hostile on the water the only thing to shoot was the tanker, and friendly fire is
     * real. The tutorial was walking first-timers into sinking their own escort.
     *
     * A single hull rather than the pair everything else opens with: this is the first
     * thing anyone shoots at, and it wants to be a target, not a fight. It arrives on a
     * clock so the lesson cannot depend on where Ardent has got to — and it comes for
     * the rig, so the tutorial's first target closes on the player instead of motoring
     * off across four kilometres of water toward the tanker.
     */
    {
      kind: 'radio', trigger: { at: 20 }, speaker: 'RADAR',
      text: 'Contact — small craft, port bow, closing. That one is not friendly.',
      read: '[alert] Contact, small craft, port bow, closing. [firmly] That one is not friendly.',
    },
    {
      kind: 'spawn', trigger: { at: 24 }, tag: 'first-blood', vessel: 'skiff',
      count: 1, rangeKm: 1.6, bearingDeg: -46, mission: 'rig',
    },
    {
      kind: 'radio', trigger: { whenWithin: { tag: 'ardent', km: 2.8 }, timeout: 600 },
      speaker: 'RADAR', text: 'Contact — two small craft, closing fast on the tanker.',
      read: '[urgent] Contact. Two small craft, closing fast on the tanker.',
    },
    {
      kind: 'spawn', trigger: { whenWithin: { tag: 'ardent', km: 2.8 }, timeout: 600 },
      tag: 'pack-a', vessel: 'skiff', count: 2, rangeKm: 1.8, bearingDeg: -60, spreadDeg: 14,
    },
    {
      kind: 'objective', trigger: { whenWithin: { tag: 'ardent', km: 2.8 }, timeout: 600 },
      text: 'Destroy the hostile craft',
    },
    /*
     * The second pair is gated on the tanker's position, not on the first pair dying.
     *
     * Chaining it to `whenResolved` quietly punished the player for shooting well: clear
     * the first pair quickly and the second arrived while Ardent was still 2 km out, so
     * the skiffs engaged her far from the rig — and a skiff attacking a ship 2 km away
     * sits about 2.2 km from the gun, past the range where the 30 mm reliably connects.
     * Killing faster made the shift harder, which is exactly backwards. Hanging it on the
     * tanker's own range instead puts the second fight at a fixed, defensible distance
     * however well or badly the first one went.
     */
    {
      kind: 'spawn', trigger: { whenWithin: { tag: 'ardent', km: 2.1 }, timeout: 420 },
      tag: 'pack-b', vessel: 'skiff', count: 2, rangeKm: 1.7, bearingDeg: -6, spreadDeg: 16,
    },
    {
      kind: 'radio', trigger: { whenWithin: { tag: 'ardent', km: 2.1 }, timeout: 420 },
      speaker: 'RADAR', text: 'Second pair, starboard quarter. They want the tanker, not you.',
      read: '[urgent] Second pair, starboard quarter. [warning] They want the tanker, not you.',
    },
    /*
     * One last hull, close in, so the tutorial's final minute has a trigger pull in it.
     *
     * It is gated tight on purpose. A shift ends when its last friendly crosses the Safe
     * Line, and whatever the player kills last decides how much empty water sits between
     * that kill and the end card — so the closer the final wave, the cleaner the finish.
     * Ardent is about four hundred metres from the line when this one goes down.
     */
    {
      kind: 'radio', trigger: { whenWithin: { tag: 'ardent', km: 1.3 }, timeout: 300 },
      speaker: 'RADAR', text: 'One more, close in, and then the board is clear.',
      read: '[steady] One more, close in. [pause] And then the board is clear.',
    },
    {
      kind: 'spawn', trigger: { whenWithin: { tag: 'ardent', km: 1.3 }, timeout: 300 },
      tag: 'straggler', vessel: 'skiff', count: 1, rangeKm: 1.4, bearingDeg: -34,
      mission: 'rig',
    },
  ],
};

/*
 * Shift 2 — night, and the campaign's second lesson: the spotlight is the weapon, radar
 * is your eyes.
 *
 * Redesigned 2026-07-28 after Adam called the first cut boring and slow; retextured
 * 2026-07-29 after he called it monotonous — ten of twelve hulls were skiffs, and every
 * one of them ran for the convoy however loudly the radio claimed otherwise. (He first
 * called it too hard as well, then withdrew that — so the pressure is preserved and
 * only the composition and the honesty changed.)
 *
 * Since the 2026-07-30 cut (Long Shadows removed) this is also the gunboat's debut, and
 * she debuts in the dark on purpose: the first the player ever sees of the class is
 * muzzle flashes from a hull the light has not found yet, which is a stronger
 * introduction than a daylight silhouette. Her radio beats already teach her cold —
 * "she has a gun, watch for the flashes".
 *
 * The fights vary in direction as well as in size: the opening pair and the far-port
 * raid carry a rig mission, closing INTO the lit water around the platform, while the
 * pushes stay on the tanker's line and the second one brings a gunboat with its skiffs
 * so it is a different problem from the first, not more of the same. The corridor spans
 * -76°..+2° (everything else is masked by the rig), so "the other side" here means its
 * eastern edge, a 64° swing off the tanker line.
 */
const shift02: ShiftScript = {
  index: 2,
  name: 'Night Collection',
  weather: 'night',
  // Seeds across the campaign are frozen at their pre-cut values: renaming one re-rolls
  // the shift's whole RNG stream, and with it spawn jitter Adam has already approved.
  seed: 'shift-03',
  maxDuration: 1100,
  beats: [
    { kind: 'objective', trigger: { at: 0 }, text: 'Night collection run' },
    {
      kind: 'radio', trigger: { at: 3 }, speaker: 'RIG CONTROL',
      text: 'No moon tonight. Radar has them long before your eyes will — put the light on anything that closes.',
      read: '[quietly] No moon tonight. [pause] Radar has them long before your eyes will. [firmly] Put the light on anything that closes.',
    },
    // 6 km, not 9.5: an eleven-minute escort. The shift should end because it is
    // finished, not because the tanker finally arrived.
    {
      kind: 'spawn', trigger: { at: 6 }, tag: 'tanker-1', vessel: 'tanker',
      count: 1, rangeKm: 6.0, bearingDeg: -8, cargo: 'oil',
    },
    /*
     * Night ranges are set by the spotlight, not by the gun.
     *
     * A skiff is only visible inside about 970 m after dark, and the spotlight reaches
     * 1.4 km — so a night engagement has to happen inside that or the player never gets
     * eyes on anything and is firing at radar markers. Staged at 2.2 km, which is fine by
     * day, every hostile in this shift stayed an unseen blip from first contact to last.
     */
    {
      kind: 'radio', trigger: { at: openingFor(2).radioAt }, speaker: 'RADAR',
      text: 'Two contacts, close in, port bow. No lights on them.',
      read: '[tense] Two contacts, close in, port bow. [quietly] No lights on them.',
    },
    ...openingBeats(2),
    {
      kind: 'objective', trigger: { at: openingFor(2).spawnAt },
      text: 'Use the light — find them before they find her',
    },
    // First raid, far port quarter, and it comes for the rig: the light has to leave
    // the tanker line to find something that is closing on the player themselves.
    {
      kind: 'radio', trigger: { at: 200 }, speaker: 'RADAR',
      text: 'Two more, far port quarter. They are testing the other side.',
      read: '[wary] Two more, far port quarter. [pause] They are testing the other side.',
    },
    {
      kind: 'spawn', trigger: { at: 205 }, tag: 'raid-a',
      vessel: 'skiff', count: 2, rangeKm: 1.4, bearingDeg: -70, spreadDeg: 10,
      mission: 'rig',
    },
    /*
     * The gunboat's first appearance in the game, and at night she is the best image
     * this shift can offer — muzzle flashes from a hull the player cannot see. A single
     * hull, met on the player's terms only if they can hold the light on her.
     */
    {
      kind: 'radio', trigger: { at: 300 }, speaker: 'RADAR',
      text: 'Heavier return, holding at two kilometres. She has a gun — watch for the flashes.',
      read: '[concerned] Heavier return, holding at two kilometres. [pause] She has a gun. [urgent] Watch for the flashes.',
    },
    {
      kind: 'spawn', trigger: { at: 305 }, tag: 'gunboat-1',
      vessel: 'gunboat', count: 1, rangeKm: 1.9, bearingDeg: -12,
    },
    // The push on the tanker's own vector, as she closes.
    {
      kind: 'radio', trigger: { whenWithin: { tag: 'tanker-1', km: 3.0 }, timeout: 450 },
      speaker: 'RADAR', text: 'Three boats, straight down her line. This is the push.',
      read: '[urgent] Three boats, straight down her line. [emphatic] This is the push.',
    },
    {
      kind: 'spawn', trigger: { whenWithin: { tag: 'tanker-1', km: 3.0 }, timeout: 450 },
      tag: 'pack-a', vessel: 'skiff', count: 3, rangeKm: 1.3, bearingDeg: -30, spreadDeg: 14,
    },
    /*
     * Gated on the tanker's range rather than on pack-a dying.
     *
     * `whenResolved` is the documented accuracy-punisher: clear the first push quickly
     * and the second one simply waited, so shooting well bought empty water. Six hundred
     * metres of the tanker's own run separates these two now, which means the second
     * push is already due about the time the first is finished however the first went.
     *
     * A gunboat leads it now (Adam, 2026-07-29: too samey to be all skiffs): the second
     * push wants to be a different problem from the first, and a bigger hull firing on
     * the tanker is one the player can actually see at night — the muzzle flashes give
     * her away. The two skiffs keep the swarm pressure the first cut had.
     */
    {
      kind: 'spawn', trigger: { whenWithin: { tag: 'tanker-1', km: 2.4 }, timeout: 450 },
      tag: 'pack-b', vessel: 'skiff', count: 2, rangeKm: 1.35, bearingDeg: -28, spreadDeg: 14,
    },
    {
      kind: 'spawn', trigger: { whenWithin: { tag: 'tanker-1', km: 2.4 }, timeout: 450 },
      tag: 'pack-b-gun', vessel: 'gunboat', count: 1, rangeKm: 1.5, bearingDeg: -18,
    },
    // Close enough to arrive during the endgame rather than after it, carrying the
    // ammunition a night of shooting has spent.
    {
      kind: 'spawn', trigger: { at: 240 }, tag: 'supply-1', vessel: 'supply',
      count: 1, rangeKm: 4.0, bearingDeg: -34, cargo: 'ammo',
    },
    // One last hull out of the dark, close enough that the light can still find her —
    // and a gunboat rather than skiffs, because muzzle flashes are the night's best image.
    {
      kind: 'spawn', trigger: { whenWithin: { tag: 'supply-1', km: 1.35 }, timeout: 400 },
      tag: 'closer', vessel: 'gunboat', count: 1, rangeKm: 1.3, bearingDeg: -44,
    },
  ],
};

import { LATE_SHIFTS } from './late';
import {
  generateEndlessShift, isEndless, endlessWatch, type EndlessLoadout,
} from './endless';

export const SHIFTS: readonly ShiftScript[] = [shift01, shift02, ...LATE_SHIFTS];

/**
 * @param loadout Endless only, and optional: what the player is actually carrying, so a
 *   generated watch never fields a hull their magazine cannot hurt. Omitted, a watch
 *   generates as though fully equipped — which is what the validation tests want, and
 *   what the authored campaign has no use for.
 */
export function shiftByIndex(index: number, loadout?: EndlessLoadout): ShiftScript {
  // Endless watches are generated on demand rather than shipped as data.
  if (isEndless(index)) return generateEndlessShift(endlessWatch(index), loadout);
  return SHIFTS.find((s) => s.index === index) ?? (SHIFTS[0] as ShiftScript);
}

export { isEndless, endlessWatch, generateEndlessShift, type EndlessLoadout };

/** How many shifts are actually playable today. */
export const SHIFT_COUNT = SHIFTS.length;
